import { ProductFormTabs, routePaths, VariantFormTabs } from "./constants.ts";
import React from "react";
import { ResourceEnum } from "src/constants/constants.ts";
import type { ModuleOptions } from "src/system/module.types.ts";
import { type ProductResource, ProductType } from "@exonn/js-sdk";
import type { RouteObject } from "react-router-dom";
import { EditProductTabTitle } from "src/admin/modules/product/components/EditProductTabTitle.tsx";

const variantsRoute: RouteObject = {
  lazy: () =>
    import(
      "src/admin/modules/product/routes/edit/tabs/variants/variants.route.tsx"
    ),
  children: [
    {
      index: true,
      lazy: () =>
        import(
          "src/admin/modules/product/routes/edit/tabs/variants/list/list.variants.tsx"
        ),
    },
    {
      path: routePaths.edit.tab(ProductFormTabs.Variants).tab(":variant_id")
        .root,
      lazy: () =>
        import(
          "src/admin/modules/product/routes/edit/tabs/variants/edit/edit.variants.route.tsx"
        ),
      children: [
        {
          index: true,
          lazy: () =>
            import(
              "src/admin/modules/product/routes/edit/tabs/variants/edit/tabs/general/general.route.tsx"
            ),
        },
        {
          path: routePaths.edit
            .tab(ProductFormTabs.Variants)
            .tab(":variant_id")
            .tab(VariantFormTabs.Attributes).root,
          lazy: () =>
            import(
              "src/admin/modules/product/routes/edit/tabs/variants/edit/tabs/attributes/attributes.route.tsx"
            ),
        },
        {
          path: routePaths.edit
            .tab(ProductFormTabs.Variants)
            .tab(":variant_id")
            .tab(VariantFormTabs.Picture).root,
          lazy: () =>
            import(
              "src/admin/modules/product/routes/edit/tabs/variants/edit/tabs/picture/picture.route.tsx"
            ),
        },
        {
          path: routePaths.edit
            .tab(ProductFormTabs.Variants)
            .tab(":variant_id")
            .tab(VariantFormTabs.Components).root,
          lazy: () =>
            import(
              "src/admin/modules/product/routes/edit/tabs/variants/edit/tabs/components/components.route.tsx"
            ),
        },
        {
          path: routePaths.edit
            .tab(ProductFormTabs.Variants)
            .tab(":variant_id")
            .tab(VariantFormTabs.Storage).root,
          lazy: () =>
            import(
              "src/admin/modules/product/routes/edit/tabs/variants/edit/tabs/storage/storage.route.tsx"
            ),
        },
        {
          path: routePaths.edit
            .tab(ProductFormTabs.Variants)
            .tab(":variant_id")
            .tab(VariantFormTabs.EventTicket).root,
          lazy: () =>
            import(
              "src/admin/modules/product/routes/edit/tabs/variants/edit/tabs/event-ticket/event-ticket.route.tsx"
            ),
        },
      ],
    },
    {
      path: routePaths.edit.tab(ProductFormTabs.Variants).tab("create").root,
      lazy: () =>
        import(
          "src/admin/modules/product/routes/edit/tabs/variants/create/create.variant.route.tsx"
        ),
    },
  ],
};

const eventTicketRoute: RouteObject = {
  lazy: () =>
    import(
      "src/admin/modules/product/routes/edit/tabs/event-ticket/event-ticket.route.tsx"
    ),
};

const componentsRoute: RouteObject = {
  lazy: () =>
    import(
      "src/admin/modules/product/routes/edit/tabs/set-components/set-components.route.tsx"
    ),
};

const inventoryRoute: RouteObject = {
  lazy: () =>
    import(
      "src/admin/modules/product/routes/edit/tabs/inventory/inventory.route.tsx"
    ),
  children: [
    {
      index: true,
      lazy: () =>
        import(
          "src/admin/modules/product/routes/edit/tabs/inventory/tabs/list/inventory.list.route.tsx"
        ),
    },
    {
      path: routePaths.edit.tab(ProductFormTabs.Inventory).tab("goods-movement")
        .root,
      lazy: () =>
        import(
          "src/admin/modules/product/routes/edit/tabs/inventory/tabs/goods-movement/goods-movement.route.tsx"
        ),
    },
    {
      path: routePaths.edit.tab(ProductFormTabs.Inventory).tab("storage").root,
      lazy: () =>
        import(
          "src/admin/modules/product/routes/edit/tabs/inventory/tabs/storage/storage.route.tsx"
        ),
    },
  ],
};

export const module: ModuleOptions<ProductResource> = {
  resource: ResourceEnum.Product,
  layoutForm: true,
  bigIcon: <i className="icon-20-box-alt" />,
  smallIcon: <i className="icon-15-box-alt" />,

  tabs: {
    edit: {
      getTitle: (resource) => {
        return resource.sku;
      },
      TitleComponent: EditProductTabTitle,
    },
  },

  formTabs: {
    [ProductFormTabs.General]: {
      title: (t) => t("tabs.general"),
    },
    [ProductFormTabs.Variants]: {
      title: (t) => t("tabs.variants"),
      isHidden: ({ values }) => {
        return ![ProductType.MULTIPLE, ProductType.COMPLEX].includes(
          values.product_type,
        );
      },
      routeObject: variantsRoute,
    },
    [ProductFormTabs.Components]: {
      title: (t) => t("tabs.components"),
      isHidden: ({ values }) => {
        return values.product_type !== ProductType.SET;
      },
      routeObject: componentsRoute,
    },
    [ProductFormTabs.Attributes]: {
      title: (t) => t("tabs.attributes"),
      isHidden: ({ values }) => {
        return [ProductType.SET, ProductType.COMPLEX].includes(
          values.product_type,
        );
      },
    },
    [ProductFormTabs.Picture]: {
      title: (t) => t("tabs.picture"),
    },
    [ProductFormTabs.EventTicket]: {
      title: (t) => t("tabs.eventTicket"),
      isHidden: ({ values, globalSettings }) => {
        return (
          !globalSettings.event_tickets_enabled ||
          ![ProductType.SINGLE, ProductType.SET].includes(values.product_type)
        );
      },
      routeObject: eventTicketRoute,
    },
    [ProductFormTabs.Inventory]: {
      title: (t) => t("tabs.inventory"),
      routeObject: inventoryRoute,
    },
  },
};
